import React, { useMemo } from 'react'
import { css } from '@emotion/core'
import { Paragraph, Separator, colors } from '@fs/zion-ui'
import { NoticeLoadingMessage } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { parseAiResponseMemoized } from '../../utils/parseAiResponse'
import { errorCollector } from '../../../../../shared/utils/utils'

// Extracted Components
const TypingCursor = () => <span className="typing-cursor" />

const cursorAnimation = css`
  @keyframes blink {
    0%,
    50% {
      opacity: 1;
    }
    51%,
    100% {
      opacity: 0;
    }
  }

  .typing-cursor {
    display: inline-block;
    background-color: ${colors.gray60};
    width: 2px;
    height: 1.2em;
    margin-inline-start: 2px;
    animation: blink 1s steps(1) infinite;
    vertical-align: text-bottom;
  }

  /* Smooth text appearance */
  p,
  li {
    transition: opacity 0.15s ease-in-out;
  }
`

const SummaryListBlock = ({ title, items, showCursor, isLastItem, sectionEncountered }) => {
  // Only show the section if it has been encountered in the stream
  if (!sectionEncountered) return null

  return (
    <>
      <Separator size="nano" />
      <Paragraph>
        {title}
        {showCursor && isLastItem && !items?.length && <TypingCursor />}
      </Paragraph>
      <Separator size="nano" />
      {items?.length > 0 && (
        <ul>
          {items.map((item) => (
            <li key={item}>
              {item}
              {showCursor && item === items[items.length - 1] && isLastItem && <TypingCursor />}
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

const getDefaultParsedData = () => ({
  namesAndRelationships: [],
  otherPersonsMentioned: [],
  dates: [],
  summary: '',
  currentSection: 'summary',
  sectionsEncountered: {
    summary: false,
    dates: false,
    names: false,
    otherPersonsMentioned: false,
  },
})

const StreamingFormattedContent = ({ streamingContent, isStreaming }) => {
  const [t] = useTranslation()

  // Parse content only when streamingContent changes
  const parsedData = useMemo(() => {
    try {
      if (streamingContent) {
        const result = parseAiResponseMemoized(streamingContent, {
          trackSections: true,
          currentSection: 'summary',
        })

        if (result?.error) {
          errorCollector(result.error, 'StreamingFormattedContent', { errorType: 'Parser error (returned)' })
          return getDefaultParsedData()
        }

        return result
      }
    } catch (error) {
      errorCollector(error, 'StreamingFormattedContent', { errorType: 'Parser error' })
    }

    return getDefaultParsedData()
  }, [streamingContent])

  const { summary, dates, namesAndRelationships, otherPersonsMentioned, currentSection, sectionsEncountered } =
    parsedData

  // Show loading dot only when no content has arrived yet
  const showLoadingDot = isStreaming && !streamingContent

  return (
    <div css={cursorAnimation}>
      <span style={{ color: colors.gray70 }}>
        <p>{t('full-text.ai-generated', 'AI-generated from the transcription below.')}</p>
      </span>
      <Separator size="nano" />

      {showLoadingDot ? (
        <NoticeLoadingMessage size="md" />
      ) : (
        <>
          {/* Names and Relationships Section */}
          <SummaryListBlock
            title={t('full-text.names-and-relationships', 'Names and Relationships')}
            items={namesAndRelationships}
            showCursor={isStreaming}
            isLastItem={currentSection === 'names'}
            sectionEncountered={sectionsEncountered?.names}
          />

          {/* Other Persons Mentioned Section */}
          <SummaryListBlock
            title={t('full-text.other-persons-mentioned', 'Other Persons Mentioned')}
            items={otherPersonsMentioned}
            showCursor={isStreaming}
            isLastItem={currentSection === 'otherPersonsMentioned'}
            sectionEncountered={sectionsEncountered?.otherPersonsMentioned}
          />

          {/* Dates Section */}
          <SummaryListBlock
            title={t('full-text.dates', 'Dates')}
            items={dates}
            showCursor={isStreaming}
            isLastItem={currentSection === 'dates'}
            sectionEncountered={sectionsEncountered?.dates}
          />

          {/* Summary Section */}
          {summary && (
            <>
              <Separator size="nano" />
              <Paragraph>{t('full-text.summary', 'Summary')}</Paragraph>
              <Separator size="nano" />
              <Paragraph size="sm">
                {summary}
                {isStreaming && currentSection === 'summary' && <TypingCursor />}
              </Paragraph>
            </>
          )}
        </>
      )}

      <Separator size="nano" />
    </div>
  )
}

export default StreamingFormattedContent
